import { useSelector } from "react-redux";

import usePopulateItems from "@hooks/cart/use-populate-items";
import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";
import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";
import useEmptyCart from "@hooks/cart/use-empty-cart";

import { usePreviewModeContext } from "@contexts/preview-mode-context";

import { isByoProduct, isCollectionTypeProduct } from "@helpers/products/types";

import { getCartProductZoneList } from "@selectors/cart";

import { ITEM_PRODUCT_TYPES, ORDER_TYPES } from "@constants/orders/cart";

const emptyFunc = () => {};

const useManipulateCartProduct = ({ emptyCartSuccessCb = emptyFunc } = {}) => {
  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const cartProductZoneList = useSelector(getCartProductZoneList);

  const {
    updateItemInLocalCart,
    deleteItemFromLocalCart,
    resetLocalCart,
    deleteSelectedCountryItemsInLocalCart,
  } = usePopulateLocalItems();

  const {
    updateItemInCart,
    updateCollectionItemInCart,
    deleteItemFromCart,
    deleteCollectionItemFromCart,
    updateByoItemFromCart,
    deleteByoItemFromCart,
  } = usePopulateItems();

  const { deleteItemFromCart: deleteBulkItemFromCart, updateItemInCart: updateBulkItemInCart } =
    useBatchPopulateItems({
      type: ORDER_TYPES.organizer,
    });
  const {
    loading: emptyingCart,
    emptyCart,
    emptyLocalCart,
  } = useEmptyCart({ successCb: emptyCartSuccessCb });

  const removeProductFromCart = ({ lineItem, intent, zoneId = "" }) => {
    const { productId, id, storeId, isBulkAvailable, hasTierPricing, productIds } = lineItem;
    const bulkAvailableOrTierPricing = isBulkAvailable || hasTierPricing;
    if (isGuestOrPreviewMode) {
      if (isByoProduct(intent)) {
        return deleteItemFromLocalCart({ medleyItemSetId: id, storeId });
      }
      return deleteItemFromLocalCart({
        productId: bulkAvailableOrTierPricing ? productIds : productId,
        storeId,
      });
    }
    if (isByoProduct(intent)) return deleteByoItemFromCart({ medleyItemSetId: id, intent, zoneId });

    if (isCollectionTypeProduct(intent)) {
      return deleteCollectionItemFromCart({ orderProductSetId: id, intent, zoneId });
    }
    if (bulkAvailableOrTierPricing) {
      const payload = productIds.map((id) => ({
        product_id: id,
        quantity: 0,
        product_type: ITEM_PRODUCT_TYPES.product,
        store_id: storeId,
        zone_id: zoneId,
      }));
      deleteBulkItemFromCart(payload);
    } else {
      return deleteItemFromCart({ productId, intent, storeId, zoneId });
    }
  };

  const onProductChangeQuantity = (payload, intent) => {
    if (isGuestOrPreviewMode) return updateItemInLocalCart({ ...payload, intent });
    if (isByoProduct(intent)) return updateByoItemFromCart({ ...payload, intent });
    if (isCollectionTypeProduct(intent)) {
      return updateCollectionItemInCart({ ...payload, intent });
    }
    if (payload?.isBulkAvailable) {
      const { productId, quantity, storePrice, price, storeId, zoneId, vendorId } = payload;

      const updatedPayload = [
        {
          product_id: productId,
          quantity,
          storePrice,
          price,
          product_type: ITEM_PRODUCT_TYPES.product,
          store_id: storeId,
          zone_id: zoneId,
          vendor_id: vendorId,
        },
      ];
      return updateBulkItemInCart(updatedPayload);
    } else {
      return updateItemInCart({ ...payload, intent });
    }
  };

  const removeAllProductsFromCart = (orderId, zoneId = "") => {
    if (isGuestOrPreviewMode) {
      if (cartProductZoneList.length > 1 && zoneId)
        return deleteSelectedCountryItemsInLocalCart(zoneId);
      emptyLocalCart();
      resetLocalCart();
    } else emptyCart(orderId, { zone_id: zoneId, respose_type: "cumulative_cart" });
  };

  return {
    emptyingCart,
    onProductChangeQuantity,
    removeProductFromCart,
    removeAllProductsFromCart,
  };
};
export default useManipulateCartProduct;
